import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  Box,
  Columns,
  Column,
  Breadcrumb,
  Bleed,
  IconChevronLeft,
  Button,
  Inline
} from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, AnimatePresenceProps } from 'framer-motion'
import { PageTitle } from '@/features/Application/elements'
import { showInfoModal, getModalInfo } from '@/features/Application/info-modal'
import client from '@/ports/api-client'
import PageTitleBar from '@/components/page-title'
import useMediaQuery from '@/hooks/use-media-query'
import { View as CreditSummaryView } from '@/features/Application/credit-summary'
import { View as RiskAssessmentView } from '@/features/Application/risk-assessment'
import { View as TrustworthinessView } from '@/features/Application/trustworthiness'
import { View as RentToIncomeView } from '@/features/Application/rent-to-income'
import TrustScoreSummary from '@/features/Application/trust-score/view/trust-score-summary'
import Background from './standalone-background.svg'
import BackgroundMobile from './standalone-background-mobile.svg'
import ApplicantInfo from '../ApplicantInfo'

interface NewAnimatePresenceProps extends Omit<AnimatePresenceProps, 'children'> {
  children: React.ReactNode
}

const AnimatedComponent = ({ children }: { children: React.ReactNode }) => {
  const NewAnimatePresence: React.FC<NewAnimatePresenceProps> = AnimatePresence

  return <NewAnimatePresence mode="wait">{children}</NewAnimatePresence>
}

const StandloneView = ({ history }: { history: any }) => {
  const isMobile = useMediaQuery(`(max-width: 991px)`)
  const { t } = useTranslation()
  const { trustScoreId: id } = useParams<{ trustScoreId: string }>()

  const getStandaloneReport = async () => {
    const response = await client.get(`/trust_score/standalone/${id}/report`)
    return response
  }

  const { isLoading: isReportLoading, data: standalone } = useQuery({
    queryKey: ['standalone', id],
    queryFn: getStandaloneReport,
    staleTime: 30000,
    enabled: true,
    refetchOnWindowFocus: false
  })

  const standaloneReport = standalone?.report
  const user = standalone?.user

  const trusworthinessTransformedData = {
    score: standaloneReport?.trustworthiness?.score,
    max_score: standaloneReport?.trustworthiness?.max_score,
    rate: standaloneReport?.trustworthiness?.rate,
    identity: {
      rate: standaloneReport?.trustworthiness?.identity?.rate,
      details: [
        {
          type: 'identification_document',
          status: standaloneReport?.trustworthiness?.identity?.details[0]?.condition
        }
      ]
    },
    income: {
      rate: standaloneReport?.trustworthiness?.income?.rate,
      details: [
        {
          type: 'document',
          status: standaloneReport?.trustworthiness?.income?.details[0]?.condition
        }
      ]
    },
    social: {
      rate: standaloneReport?.trustworthiness?.social?.rate,
      details: [
        {
          type: 'LinkedIn Account',
          status: standaloneReport?.trustworthiness?.social?.details[0]?.condition
        }
      ]
    },
    employment: {
      rate: standaloneReport?.trustworthiness?.employment?.rate,
      details: [
        {
          type: 'document',
          status: standaloneReport?.trustworthiness?.employment?.details[0]?.condition
        }
      ]
    }
  }

  const isLocked = false

  const modalInfo = getModalInfo('trust-score', t)

  const handleShowInfoModal = () =>
    showInfoModal({
      title: modalInfo?.title,
      content: modalInfo?.content,
      mobileContent: modalInfo?.mobileContent,
      isPoweredByEquifax: modalInfo?.isPoweredByEquifax
    })

  const applicantFirstName = user ? `${user.first_name} ${user.last_name.slice(0, 1)}.` : ''
  const StandaloneBreadcrumbs = (
    <Box
      paddingY="xsmall"
      paddingX="large"
      position="sticky"
      width="full"
      background="white"
      borderBottom="borderNeutralLight"
    >
      <Breadcrumb
        items={[
          {
            label: t('l.listing.application.liv_score.text', 'Trust Score'),
            href: `/trust-score/`
          },
          {
            label: applicantFirstName
          }
        ]}
      />
    </Box>
  )

  return (
    <AnimatedComponent>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flexStart"
        background="white"
        style={{
          backgroundImage: isMobile ? `url(${BackgroundMobile})` : `url(${Background})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain'
        }}
      >
        <PageTitleBar customChild={!isMobile ? StandaloneBreadcrumbs : null}>
          {t('l.standalone_trustscore.list.title', 'Trust Score')}
        </PageTitleBar>
        <Box
          padding={{ mobile: 'gutter', desktop: 'xlarge' }}
          marginTop={{ mobile: 'none', tablet: 'xlarge' }}
          maxWidth="xlarge"
        >
          <Columns collapseBelow="desktop" space="gutter">
            <Column width="content">
              <Box
                display="flex"
                flexDirection="column"
                gap="gutter"
                position="sticky"
                style={{ top: '130px' }}
              >
                {isMobile && (
                  <Inline space="small">
                    <Button onClick={() => history?.push(`/trust-score/`)} variant="transparent">
                      <Bleed left="medium">
                        <IconChevronLeft />
                        {t('l.standalone_trustscore.list.title', 'Trust Score')}
                      </Bleed>
                    </Button>
                  </Inline>
                )}
                <ApplicantInfo
                  applicationDate={user?.updated_at}
                  data={user}
                  isFavourite={user?.is_favourite}
                  loading={isReportLoading}
                  reportId={id}
                />
              </Box>
            </Column>
            <Column>
              <PageTitle
                title={t('b.application.view.tenancy_application.liv_score.title', 'Trust Score')}
                handleClick={handleShowInfoModal}
                noMobileMarginTop={true}
                showInfo
                showPoweredBy
                clickId="application-trust-score-info-score"
              />
              <TrustScoreSummary
                score={standaloneReport?.score}
                rate={standaloneReport?.rate}
                state={'unlocked'}
                applicationId={standaloneReport?.id}
                status={standaloneReport?.status}
                reason={standaloneReport?.user_questions?.no_income_reason?.answer}
                applicantName={`${user?.first_name} ${user?.last_name?.substring(0, 1)}`}
                applicantFullName={`${user?.first_name} ${user?.last_name?.substring(0, 1)}`}
                hasUnlockCredits={true}
                isLoading={isReportLoading}
                requestQueued={standaloneReport?.is_request_queued}
              />
              <CreditSummaryView
                data={{
                  ...standaloneReport?.credit_summary,
                  credit_score_range: standaloneReport?.credit_score_range,
                  status: standaloneReport?.status
                }}
                application={standaloneReport}
                isLocked={isLocked}
                isLoading={isReportLoading}
                standalone
              />
              <RiskAssessmentView
                data={standaloneReport?.risk_assessment}
                application={standaloneReport}
                isLocked={isLocked}
                isLoading={isReportLoading}
                applicantFullName={`${user?.first_name} ${user?.last_name?.substring(0, 1)}`}
                hasUnlockCredits={true}
                standalone
              />
              <TrustworthinessView
                data={trusworthinessTransformedData}
                standalone
                application={standaloneReport}
                isLocked={isLocked}
                isLoading={isReportLoading}
              />
              <RentToIncomeView
                data={standaloneReport?.income_to_rent_ratio}
                application={standaloneReport}
                isLocked={isLocked}
                isLoading={isReportLoading}
                standalone
              />
            </Column>
          </Columns>
        </Box>
      </Box>
    </AnimatedComponent>
  )
}

export default StandloneView
